import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    Check,
    Pencil,
    Plus,
    Trash,
    UserCircle,
    Warning
} from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";

interface UserProfileSectionProps {
  currentUser: any;
  employeeProfiles: any[];
  setEmployeeProfiles: (profiles: any[]) => void;
}

export function UserProfileSection({
  currentUser,
  employeeProfiles,
  setEmployeeProfiles,
}: UserProfileSectionProps) {

  // Find or create current profile
  const currentProfile = employeeProfiles.find(p => p.employeeId === currentUser?.id) || {
    employeeId: currentUser?.id || '',
    personalInfo: {},
    contactInfo: {},
    emergencyContacts: {},
    skills: [],
    certifications: [],
    education: [],
    experience: [],
    completionPercentage: 0,
    lastUpdated: new Date().toISOString()
  };

  // Calculate completion percentage
  const calculateCompletion = (profile: typeof currentProfile): number => {
    let total = 0;
    let filled = 0;

    // Personal Info (20%)
    const personalFields = ['dateOfBirth', 'gender', 'bloodGroup', 'maritalStatus', 'personalEmail', 'personalPhone'];
    personalFields.forEach(field => {
      total += 20 / personalFields.length;
      if (profile.personalInfo[field as keyof typeof profile.personalInfo]) filled += 20 / personalFields.length;
    });

    // Contact Info (15%)
    const contactFields = ['currentAddress', 'city', 'state', 'zipCode', 'country'];
    contactFields.forEach(field => {
      total += 15 / contactFields.length;
      if (profile.contactInfo[field as keyof typeof profile.contactInfo]) filled += 15 / contactFields.length;
    });

    // Emergency Contacts (15%)
    if (profile.emergencyContacts.primary) filled += 15;
    total += 15;

    // Banking Details (15%)
    if (profile.bankingDetails) filled += 15;
    total += 15;

    // Skills (10%)
    if (profile.skills.length > 0) filled += 10;
    total += 10;

    // Certifications (5%)
    if (profile.certifications.length > 0) filled += 5;
    total += 5;

    // Education (10%)
    if (profile.education.length > 0) filled += 10;
    total += 10;

    // Experience (10%)
    if (profile.experience.length > 0) filled += 10;
    total += 10;

    return Math.round((filled / total) * 100);
  };

  const completionPercentage = calculateCompletion(currentProfile);
  const completionBadge = completionPercentage < 41 ? 'Bronze' : completionPercentage < 71 ? 'Silver' : completionPercentage < 91 ? 'Gold' : 'Platinum';
  const badgeColor = completionBadge === 'Bronze' ? 'bg-orange-100 text-orange-700' : completionBadge === 'Silver' ? 'bg-slate-100 text-slate-700' : completionBadge === 'Gold' ? 'bg-yellow-100 text-yellow-700' : 'bg-indigo-100 text-indigo-700';

  const [profileTab, setProfileTab] = useState<'personal' | 'contact' | 'emergency' | 'banking' | 'skills' | 'education' | 'experience'>('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState(currentProfile);

  const saveProfile = () => {
    const updatedProfile = {
      ...editedProfile,
      completionPercentage: calculateCompletion(editedProfile),
      lastUpdated: new Date().toISOString()
    };

    const existingIndex = employeeProfiles.findIndex(p => p.employeeId === currentUser?.id);
    if (existingIndex >= 0) {
      const updated = [...employeeProfiles];
      updated[existingIndex] = updatedProfile;
      setEmployeeProfiles(updated);
    } else {
      setEmployeeProfiles([...employeeProfiles, updatedProfile]);
    }

    setIsEditing(false);
    toast.success('Profile updated successfully!');
  };

  return (
    <div className="space-y-6">
      {/* Profile Overview Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCircle className="w-5 h-5" />
            My Profile
          </CardTitle>
          <CardDescription>Manage your personal information and professional details</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            {/* Profile Avatar */}
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center text-4xl">
                {currentUser?.avatar || '👤'}
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1 space-y-4">
              <div>
                <h3 className="text-2xl font-bold">{currentUser?.name}</h3>
                <p className="text-muted-foreground">{currentUser?.role.charAt(0).toUpperCase() + currentUser?.role.slice(1)} • {currentUser?.department}</p>
              </div>

              {/* Completion Progress */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Profile Completion</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">{completionPercentage}%</span>
                    <Badge className={badgeColor}>{completionBadge}</Badge>
                  </div>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${completionPercentage}%` }}
                  />
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 pt-2">
                <div>
                  <div className="text-2xl font-bold">{currentProfile.skills.length}</div>
                  <div className="text-xs text-muted-foreground">Skills</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{currentProfile.certifications.length}</div>
                  <div className="text-xs text-muted-foreground">Certificates</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{currentProfile.experience.length}</div>
                  <div className="text-xs text-muted-foreground">Experience</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Sections Tabs */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'personal', label: 'Personal Info' },
              { id: 'contact', label: 'Contact' },
              { id: 'emergency', label: 'Emergency' },
              { id: 'banking', label: 'Banking' },
              { id: 'skills', label: 'Skills' },
              { id: 'education', label: 'Education' },
              { id: 'experience', label: 'Experience' },
            ].map((tab) => (
              <Button
                key={tab.id}
                variant={profileTab === tab.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setProfileTab(tab.id as any);
                  setIsEditing(false);
                }}
              >
                {tab.label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Edit Mode Toggle */}
          <div className="flex justify-end">
            {isEditing ? (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => {
                  setIsEditing(false);
                  setEditedProfile(currentProfile);
                }}>
                  Cancel
                </Button>
                <Button size="sm" onClick={saveProfile}>
                  <Check className="w-4 h-4 mr-1" />
                  Save Changes
                </Button>
              </div>
            ) : (
              <Button size="sm" onClick={() => {
                setIsEditing(true);
                setEditedProfile(currentProfile);
              }}>
                <Pencil className="w-4 h-4 mr-1" />
                Edit
              </Button>
            )}
          </div>

          {/* Personal Information Tab */}
          {profileTab === 'personal' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Date of Birth</label>
                  <Input
                    type="date"
                    value={isEditing ? (editedProfile.personalInfo.dateOfBirth || '') : (currentProfile.personalInfo.dateOfBirth || '')}
                    onChange={(e) => setEditedProfile({...editedProfile, personalInfo: {...editedProfile.personalInfo, dateOfBirth: e.target.value}})}
                    disabled={!isEditing}
                    className="text-base"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Gender</label>
                  <select
                    value={isEditing ? (editedProfile.personalInfo.gender || '') : (currentProfile.personalInfo.gender || '')}
                    onChange={(e) => setEditedProfile({...editedProfile, personalInfo: {...editedProfile.personalInfo, gender: e.target.value as any}})}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded-md text-base"
                  >
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Blood Group</label>
                  <Input
                    value={isEditing ? (editedProfile.personalInfo.bloodGroup || '') : (currentProfile.personalInfo.bloodGroup || '')}
                    onChange={(e) => setEditedProfile({...editedProfile, personalInfo: {...editedProfile.personalInfo, bloodGroup: e.target.value}})}
                    disabled={!isEditing}
                    placeholder="e.g., O+"
                    className="text-base"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Marital Status</label>
                  <select
                    value={isEditing ? (editedProfile.personalInfo.maritalStatus || '') : (currentProfile.personalInfo.maritalStatus || '')}
                    onChange={(e) => setEditedProfile({...editedProfile, personalInfo: {...editedProfile.personalInfo, maritalStatus: e.target.value as any}})}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded-md text-base"
                  >
                    <option value="">Select...</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Personal Email</label>
                  <Input
                    type="email"
                    value={isEditing ? (editedProfile.personalInfo.personalEmail || '') : (currentProfile.personalInfo.personalEmail || '')}
                    onChange={(e) => setEditedProfile({...editedProfile, personalInfo: {...editedProfile.personalInfo, personalEmail: e.target.value}})}
                    disabled={!isEditing}
                    placeholder="personal@example.com"
                    className="text-base"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Personal Phone</label>
                  <Input
                    type="tel"
                    value={isEditing ? (editedProfile.personalInfo.personalPhone || '') : (currentProfile.personalInfo.personalPhone || '')}
                    onChange={(e) => setEditedProfile({...editedProfile, personalInfo: {...editedProfile.personalInfo, personalPhone: e.target.value}})}
                    disabled={!isEditing}
                    placeholder="+1 (555) 000-0000"
                    className="text-base"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Contact Information Tab */}
          {profileTab === 'contact' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Contact Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Current Address</label>
                  <Input
                    value={isEditing ? (editedProfile.contactInfo.currentAddress || '') : (currentProfile.contactInfo.currentAddress || '')}
                    onChange={(e) => setEditedProfile({...editedProfile, contactInfo: {...editedProfile.contactInfo, currentAddress: e.target.value}})}
                    disabled={!isEditing}
                    placeholder="Street address"
                    className="text-base"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">City</label>
                    <Input
                      value={isEditing ? (editedProfile.contactInfo.city || '') : (currentProfile.contactInfo.city || '')}
                      onChange={(e) => setEditedProfile({...editedProfile, contactInfo: {...editedProfile.contactInfo, city: e.target.value}})}
                      disabled={!isEditing}
                      className="text-base"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">State</label>
                    <Input
                      value={isEditing ? (editedProfile.contactInfo.state || '') : (currentProfile.contactInfo.state || '')}
                      onChange={(e) => setEditedProfile({...editedProfile, contactInfo: {...editedProfile.contactInfo, state: e.target.value}})}
                      disabled={!isEditing}
                      className="text-base"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Zip Code</label>
                    <Input
                      value={isEditing ? (editedProfile.contactInfo.zipCode || '') : (currentProfile.contactInfo.zipCode || '')}
                      onChange={(e) => setEditedProfile({...editedProfile, contactInfo: {...editedProfile.contactInfo, zipCode: e.target.value}})}
                      disabled={!isEditing}
                      className="text-base"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Country</label>
                    <Input
                      value={isEditing ? (editedProfile.contactInfo.country || '') : (currentProfile.contactInfo.country || '')}
                      onChange={(e) => setEditedProfile({...editedProfile, contactInfo: {...editedProfile.contactInfo, country: e.target.value}})}
                      disabled={!isEditing}
                      className="text-base"
                    />
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-4">Work Contact (Read-only)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Work Email</label>
                      <Input value={currentUser?.email || ''} disabled className="text-base bg-slate-50" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Work Phone</label>
                      <Input value={currentUser?.phone || 'Not set'} disabled className="text-base bg-slate-50" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Emergency Contacts Tab */}
          {profileTab === 'emergency' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Emergency Contacts</h3>
                <div className="space-y-6">
                  {/* Primary Contact */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <h4 className="font-medium">Primary Contact</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Name</label>
                        <Input
                          value={isEditing ? (editedProfile.emergencyContacts.primary?.name || '') : (currentProfile.emergencyContacts.primary?.name || '')}
                          onChange={(e) => setEditedProfile({...editedProfile, emergencyContacts: {...editedProfile.emergencyContacts, primary: {...editedProfile.emergencyContacts.primary, name: e.target.value, relationship: editedProfile.emergencyContacts.primary?.relationship || '', phone: editedProfile.emergencyContacts.primary?.phone || ''}}})}
                          disabled={!isEditing}
                          className="text-base"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Relationship</label>
                        <Input
                          value={isEditing ? (editedProfile.emergencyContacts.primary?.relationship || '') : (currentProfile.emergencyContacts.primary?.relationship || '')}
                          onChange={(e) => setEditedProfile({...editedProfile, emergencyContacts: {...editedProfile.emergencyContacts, primary: {...editedProfile.emergencyContacts.primary, name: editedProfile.emergencyContacts.primary?.name || '', relationship: e.target.value, phone: editedProfile.emergencyContacts.primary?.phone || ''}}})}
                          disabled={!isEditing}
                          placeholder="e.g., Spouse, Parent"
                          className="text-base"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium">Phone</label>
                        <Input
                          type="tel"
                          value={isEditing ? (editedProfile.emergencyContacts.primary?.phone || '') : (currentProfile.emergencyContacts.primary?.phone || '')}
                          onChange={(e) => setEditedProfile({...editedProfile, emergencyContacts: {...editedProfile.emergencyContacts, primary: {...editedProfile.emergencyContacts.primary, name: editedProfile.emergencyContacts.primary?.name || '', relationship: editedProfile.emergencyContacts.primary?.relationship || '', phone: e.target.value}}})}
                          disabled={!isEditing}
                          className="text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Secondary Contact */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <h4 className="font-medium">Secondary Contact (Optional)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Name</label>
                        <Input
                          value={isEditing ? (editedProfile.emergencyContacts.secondary?.name || '') : (currentProfile.emergencyContacts.secondary?.name || '')}
                          onChange={(e) => setEditedProfile({...editedProfile, emergencyContacts: {...editedProfile.emergencyContacts, secondary: e.target.value ? {...editedProfile.emergencyContacts.secondary, name: e.target.value, relationship: editedProfile.emergencyContacts.secondary?.relationship || '', phone: editedProfile.emergencyContacts.secondary?.phone || ''} : undefined}})}
                          disabled={!isEditing}
                          className="text-base"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Relationship</label>
                        <Input
                          value={isEditing ? (editedProfile.emergencyContacts.secondary?.relationship || '') : (currentProfile.emergencyContacts.secondary?.relationship || '')}
                          onChange={(e) => setEditedProfile({...editedProfile, emergencyContacts: {...editedProfile.emergencyContacts, secondary: editedProfile.emergencyContacts.secondary ? {...editedProfile.emergencyContacts.secondary, relationship: e.target.value} : {name: '', relationship: e.target.value, phone: ''}}})}
                          disabled={!isEditing}
                          className="text-base"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium">Phone</label>
                        <Input
                          type="tel"
                          value={isEditing ? (editedProfile.emergencyContacts.secondary?.phone || '') : (currentProfile.emergencyContacts.secondary?.phone || '')}
                          onChange={(e) => setEditedProfile({...editedProfile, emergencyContacts: {...editedProfile.emergencyContacts, secondary: editedProfile.emergencyContacts.secondary ? {...editedProfile.emergencyContacts.secondary, phone: e.target.value} : {name: '', relationship: '', phone: e.target.value}}})}
                          disabled={!isEditing}
                          className="text-base"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Banking Details Tab */}
          {profileTab === 'banking' && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <Warning className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Sensitive Information</p>
                  <p>Banking details are stored securely. Account numbers are partially masked for security.</p>
                </div>
              </div>
              <h3 className="text-lg font-semibold">Banking Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Bank Name</label>
                  <Input
                    value={isEditing ? (editedProfile.bankingDetails?.bankName || '') : (currentProfile.bankingDetails?.bankName || '')}
                    onChange={(e) => setEditedProfile({...editedProfile, bankingDetails: {...editedProfile.bankingDetails, bankName: e.target.value, accountNumber: editedProfile.bankingDetails?.accountNumber || '', ifscCode: editedProfile.bankingDetails?.ifscCode || '', branchName: editedProfile.bankingDetails?.branchName || ''}})}
                    disabled={!isEditing}
                    className="text-base"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Branch Name</label>
                  <Input
                    value={isEditing ? (editedProfile.bankingDetails?.branchName || '') : (currentProfile.bankingDetails?.branchName || '')}
                    onChange={(e) => setEditedProfile({...editedProfile, bankingDetails: {...editedProfile.bankingDetails, bankName: editedProfile.bankingDetails?.bankName || '', accountNumber: editedProfile.bankingDetails?.accountNumber || '', ifscCode: editedProfile.bankingDetails?.ifscCode || '', branchName: e.target.value}})}
                    disabled={!isEditing}
                    className="text-base"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Account Number</label>
                  <Input
                    value={isEditing ? (editedProfile.bankingDetails?.accountNumber || '') : (currentProfile.bankingDetails?.accountNumber ? `****${currentProfile.bankingDetails.accountNumber.slice(-4)}` : '')}
                    onChange={(e) => setEditedProfile({...editedProfile, bankingDetails: {...editedProfile.bankingDetails, bankName: editedProfile.bankingDetails?.bankName || '', accountNumber: e.target.value, ifscCode: editedProfile.bankingDetails?.ifscCode || '', branchName: editedProfile.bankingDetails?.branchName || ''}})}
                    disabled={!isEditing}
                    placeholder={!isEditing ? "Masked for security" : ""}
                    className="text-base"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">IFSC/Routing Code</label>
                  <Input
                    value={isEditing ? (editedProfile.bankingDetails?.ifscCode || '') : (currentProfile.bankingDetails?.ifscCode || '')}
                    onChange={(e) => setEditedProfile({...editedProfile, bankingDetails: {...editedProfile.bankingDetails, bankName: editedProfile.bankingDetails?.bankName || '', accountNumber: editedProfile.bankingDetails?.accountNumber || '', ifscCode: e.target.value, branchName: editedProfile.bankingDetails?.branchName || ''}})}
                    disabled={!isEditing}
                    className="text-base"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Skills Tab */}
          {profileTab === 'skills' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Skills & Certifications</h3>
                {isEditing && (
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditedProfile({
                      ...editedProfile,
                      skills: [...editedProfile.skills, { id: `skill-${Date.now()}`, name: '', proficiency: 'Beginner' }]
                    });
                  }}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Skill
                  </Button>
                )}
              </div>

              {/* Skills List */}
              <div className="space-y-2">
                {(isEditing ? editedProfile.skills : currentProfile.skills).length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No skills added yet</p>
                ) : (
                  (isEditing ? editedProfile.skills : currentProfile.skills).map((skill, index) => (
                    <div key={skill.id} className="flex gap-2 items-center">
                      <Input
                        value={skill.name}
                        onChange={(e) => {
                          const updated = [...editedProfile.skills];
                          updated[index] = {...skill, name: e.target.value};
                          setEditedProfile({...editedProfile, skills: updated});
                        }}
                        disabled={!isEditing}
                        placeholder="Skill name"
                        className="text-base flex-1"
                      />
                      <select
                        value={skill.proficiency}
                        onChange={(e) => {
                          const updated = [...editedProfile.skills];
                          updated[index] = {...skill, proficiency: e.target.value as any};
                          setEditedProfile({...editedProfile, skills: updated});
                        }}
                        disabled={!isEditing}
                        className="px-3 py-2 border rounded-md text-base"
                      >
                        <option value="Beginner">Beginner</option>
                        <option value="Intermediate">Intermediate</option>
                        <option value="Expert">Expert</option>
                      </select>
                      {isEditing && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            setEditedProfile({
                              ...editedProfile,
                              skills: editedProfile.skills.filter((_, i) => i !== index)
                            });
                          }}
                        >
                          <Trash className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Certifications Section */}
              <div className="pt-6 border-t">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold">Certifications</h4>
                  {isEditing && (
                    <Button size="sm" variant="outline" onClick={() => {
                      setEditedProfile({
                        ...editedProfile,
                        certifications: [...editedProfile.certifications, { id: `cert-${Date.now()}`, name: '', issuer: '', issueDate: '' }]
                      });
                    }}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Certificate
                    </Button>
                  )}
                </div>
                <div className="space-y-4">
                  {(isEditing ? editedProfile.certifications : currentProfile.certifications).length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No certifications added yet</p>
                  ) : (
                    (isEditing ? editedProfile.certifications : currentProfile.certifications).map((cert, index) => (
                      <div key={cert.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 space-y-3">
                            <Input
                              value={cert.name}
                              onChange={(e) => {
                                const updated = [...editedProfile.certifications];
                                updated[index] = {...cert, name: e.target.value};
                                setEditedProfile({...editedProfile, certifications: updated});
                              }}
                              disabled={!isEditing}
                              placeholder="Certification name"
                              className="text-base font-medium"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              <Input
                                value={cert.issuer}
                                onChange={(e) => {
                                  const updated = [...editedProfile.certifications];
                                  updated[index] = {...cert, issuer: e.target.value};
                                  setEditedProfile({...editedProfile, certifications: updated});
                                }}
                                disabled={!isEditing}
                                placeholder="Issuing organization"
                                className="text-base"
                              />
                              <Input
                                type="date"
                                value={cert.issueDate}
                                onChange={(e) => {
                                  const updated = [...editedProfile.certifications];
                                  updated[index] = {...cert, issueDate: e.target.value};
                                  setEditedProfile({...editedProfile, certifications: updated});
                                }}
                                disabled={!isEditing}
                                placeholder="Issue date"
                                className="text-base"
                              />
                              <Input
                                type="date"
                                value={cert.expiryDate || ''}
                                onChange={(e) => {
                                  const updated = [...editedProfile.certifications];
                                  updated[index] = {...cert, expiryDate: e.target.value};
                                  setEditedProfile({...editedProfile, certifications: updated});
                                }}
                                disabled={!isEditing}
                                placeholder="Expiry date (optional)"
                                className="text-base"
                              />
                            </div>
                          </div>
                          {isEditing && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => {
                                setEditedProfile({
                                  ...editedProfile,
                                  certifications: editedProfile.certifications.filter((_, i) => i !== index)
                                });
                              }}
                            >
                              <Trash className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Education Tab */}
          {profileTab === 'education' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Education History</h3>
                {isEditing && (
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditedProfile({
                      ...editedProfile,
                      education: [...editedProfile.education, { id: `edu-${Date.now()}`, degree: '', institution: '', fieldOfStudy: '', startYear: '', isCurrent: false }]
                    });
                  }}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Education
                  </Button>
                )}
              </div>
              <div className="space-y-4">
                {(isEditing ? editedProfile.education : currentProfile.education).length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No education records added yet</p>
                ) : (
                  (isEditing ? editedProfile.education : currentProfile.education).map((edu, index) => (
                    <div key={edu.id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input
                              value={edu.degree}
                              onChange={(e) => {
                                const updated = [...editedProfile.education];
                                updated[index] = {...edu, degree: e.target.value};
                                setEditedProfile({...editedProfile, education: updated});
                              }}
                              disabled={!isEditing}
                              placeholder="Degree/Qualification"
                              className="text-base font-medium"
                            />
                            <Input
                              value={edu.institution}
                              onChange={(e) => {
                                const updated = [...editedProfile.education];
                                updated[index] = {...edu, institution: e.target.value};
                                setEditedProfile({...editedProfile, education: updated});
                              }}
                              disabled={!isEditing}
                              placeholder="Institution name"
                              className="text-base"
                            />
                            <Input
                              value={edu.fieldOfStudy}
                              onChange={(e) => {
                                const updated = [...editedProfile.education];
                                updated[index] = {...edu, fieldOfStudy: e.target.value};
                                setEditedProfile({...editedProfile, education: updated});
                              }}
                              disabled={!isEditing}
                              placeholder="Field of study"
                              className="text-base"
                            />
                            <div className="flex gap-2">
                              <Input
                                value={edu.startYear}
                                onChange={(e) => {
                                  const updated = [...editedProfile.education];
                                  updated[index] = {...edu, startYear: e.target.value};
                                  setEditedProfile({...editedProfile, education: updated});
                                }}
                                disabled={!isEditing}
                                placeholder="Start year"
                                className="text-base"
                              />
                              <Input
                                value={edu.endYear || ''}
                                onChange={(e) => {
                                  const updated = [...editedProfile.education];
                                  updated[index] = {...edu, endYear: e.target.value};
                                  setEditedProfile({...editedProfile, education: updated});
                                }}
                                disabled={!isEditing || edu.isCurrent}
                                placeholder="End year"
                                className="text-base"
                              />
                            </div>
                          </div>
                          {isEditing && (
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={edu.isCurrent}
                                onChange={(e) => {
                                  const updated = [...editedProfile.education];
                                  updated[index] = {...edu, isCurrent: e.target.checked, endYear: e.target.checked ? undefined : edu.endYear};
                                  setEditedProfile({...editedProfile, education: updated});
                                }}
                              />
                              Currently studying
                            </label>
                          )}
                        </div>
                        {isEditing && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              setEditedProfile({
                                ...editedProfile,
                                education: editedProfile.education.filter((_, i) => i !== index)
                              });
                            }}
                          >
                            <Trash className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Experience Tab */}
          {profileTab === 'experience' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Work Experience</h3>
                {isEditing && (
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditedProfile({
                      ...editedProfile,
                      experience: [...editedProfile.experience, { id: `exp-${Date.now()}`, companyName: '', jobTitle: '', startDate: '', isCurrent: false }]
                    });
                  }}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Experience
                  </Button>
                )}
              </div>
              <div className="space-y-4">
                {(isEditing ? editedProfile.experience : currentProfile.experience).length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No work experience added yet</p>
                ) : (
                  (isEditing ? editedProfile.experience : currentProfile.experience).map((exp, index) => (
                    <div key={exp.id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input
                              value={exp.jobTitle}
                              onChange={(e) => {
                                const updated = [...editedProfile.experience];
                                updated[index] = {...exp, jobTitle: e.target.value};
                                setEditedProfile({...editedProfile, experience: updated});
                              }}
                              disabled={!isEditing}
                              placeholder="Job title"
                              className="text-base font-medium"
                            />
                            <Input
                              value={exp.companyName}
                              onChange={(e) => {
                                const updated = [...editedProfile.experience];
                                updated[index] = {...exp, companyName: e.target.value};
                                setEditedProfile({...editedProfile, experience: updated});
                              }}
                              disabled={!isEditing}
                              placeholder="Company name"
                              className="text-base"
                            />
                            <Input
                              type="date"
                              value={exp.startDate}
                              onChange={(e) => {
                                const updated = [...editedProfile.experience];
                                updated[index] = {...exp, startDate: e.target.value};
                                setEditedProfile({...editedProfile, experience: updated});
                              }}
                              disabled={!isEditing}
                              placeholder="Start date"
                              className="text-base"
                            />
                            <Input
                              type="date"
                              value={exp.endDate || ''}
                              onChange={(e) => {
                                const updated = [...editedProfile.experience];
                                updated[index] = {...exp, endDate: e.target.value};
                                setEditedProfile({...editedProfile, experience: updated});
                              }}
                              disabled={!isEditing || exp.isCurrent}
                              placeholder="End date"
                              className="text-base"
                            />
                          </div>
                          {isEditing && (
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={exp.isCurrent}
                                onChange={(e) => {
                                  const updated = [...editedProfile.experience];
                                  updated[index] = {...exp, isCurrent: e.target.checked, endDate: e.target.checked ? undefined : exp.endDate};
                                  setEditedProfile({...editedProfile, experience: updated});
                                }}
                              />
                              Currently working here
                            </label>
                          )}
                          <textarea
                            value={exp.responsibilities || ''}
                            onChange={(e) => {
                              const updated = [...editedProfile.experience];
                              updated[index] = {...exp, responsibilities: e.target.value};
                              setEditedProfile({...editedProfile, experience: updated});
                            }}
                            disabled={!isEditing}
                            placeholder="Key responsibilities (optional)"
                            className="w-full px-3 py-2 border rounded-md text-base min-h-[80px]"
                          />
                        </div>
                        {isEditing && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              setEditedProfile({
                                ...editedProfile,
                                experience: editedProfile.experience.filter((_, i) => i !== index)
                              });
                            }}
                          >
                            <Trash className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
